import { useEffect, useLayoutEffect, useState } from 'react';
import { WAITING_MOTION } from '@featit/shared/brand/waiting-motion';
import { LIST_LOAD_DEADLINE_MS } from './useLoadDeadline';
import { holdContentPending } from './screenSettled';
import { waitOrigin } from './pressOrigin';

/**
 * F-0604 WAIT-20 to WAIT-22 (`docs/concept-waiting-motion.md`): the stages of one wait, in one place,
 * so no screen keeps a timer of its own.
 *
 *  - `quiet`    the first `SKELETON_DELAY_MS`: a read that fast draws no placeholder at all (M-7);
 *  - `skeleton` the live skeleton, drawn only now;
 *  - `study`    from 2 s (`WAITING_MOTION.captionsMs[0]`): the session's study with a caption (WAIT-8);
 *  - `failed`   from the cold-stall deadline (`LIST_LOAD_DEADLINE_MS`, RD-33): the failure with Retry.
 *
 * The stages count from the press that started the wait (WAIT-21a, `pressOrigin`), or from the mount
 * when no press did. Any change of `key`, or the wait ending, starts again. In `quiet` the screen still says it is
 * loading, for the launch's cover to wait on (`holdContentPending`, I-1234).
 */
export type LoadStageType = 'quiet' | 'skeleton' | 'study' | 'failed';

/** M-7: *a response under 300 ms shows no skeleton at all* (`useDelayedSkeleton`'s own number). */
export const SKELETON_DELAY_MS = 300;

/** Each stage after the first and the time into the wait it starts at. */
function stagesOf(deadline: boolean): [LoadStageType, number][] {
  return [
    ['skeleton', SKELETON_DELAY_MS],
    ['study', WAITING_MOTION.captionsMs[0]],
    ...(deadline ? [['failed', LIST_LOAD_DEADLINE_MS] as [LoadStageType, number]] : []),
  ];
}

/** How long the wait has run, from the press that started it (WAIT-21a), else from now. */
function waitedSoFar(): number {
  const now = Date.now();
  return now - waitOrigin(now);
}

/** The stage a wait is in `waited` ms after its origin. */
function stageAfter(waited: number, deadline: boolean): LoadStageType {
  return (
    stagesOf(deadline)
      .filter(([, at]) => at <= waited)
      .pop()?.[0] ?? 'quiet'
  );
}

export function useLoadStages(
  waiting: boolean,
  key: string,
  /** `false` where the screen's own deadline (`useStalledLoad`) owns the failure. */
  deadline = true,
): LoadStageType {
  /*
    I-1871: the stage reached since the press is the first render's, never an effect's. Set in a
    passive effect, a pane that mounted 300 ms or more after its press committed `quiet` first, and a
    frame could paint it with clear bars and no study while its wait was past both. Worked out while
    rendering, on the mount and on a new wait (a change of `key`), it costs no extra commit.
  */
  const [wait, setWait] = useState(() => ({
    key,
    waiting,
    deadline,
    stage: waiting ? stageAfter(waitedSoFar(), deadline) : ('quiet' as LoadStageType),
  }));
  let current = wait;
  if (wait.key !== key || wait.waiting !== waiting || wait.deadline !== deadline) {
    current = {
      key,
      waiting,
      deadline,
      stage: waiting ? stageAfter(waitedSoFar(), deadline) : 'quiet',
    };
    setWait(current);
  }
  const stage = current.stage;

  useLayoutEffect(
    () => (waiting && stage === 'quiet' ? holdContentPending() : undefined),
    [waiting, stage],
  );

  useEffect(() => {
    if (!waiting) return undefined;
    const advance = (next: LoadStageType) =>
      setWait((was) => (was.key === key && was.stage !== next ? { ...was, stage: next } : was));
    // The time since the render: a stage reached in between is drawn now, the rest when it comes.
    const waited = waitedSoFar();
    advance(stageAfter(waited, deadline));
    const timers = stagesOf(deadline)
      .filter(([, at]) => at > waited)
      .map(([next, at]) => window.setTimeout(() => advance(next), at - waited));
    return () => timers.forEach(window.clearTimeout);
  }, [waiting, key, deadline]);

  return waiting ? stage : 'quiet';
}
